import { queryOptions } from "@tanstack/react-query";

import type { Money } from "@workspace/accounting/types";
import { apiUrl } from "@workspace/base-api/apiUrl";
import { fetchJson, HttpRequestError } from "@workspace/base-api/fetchJson";

/**
 * HA-836 — the UI for HA-835 (contract: conductor/reports/ha-835-contract.md). A card refund the
 * reconciler couldn't settle and flagged for a person.
 */
export type RefundAttentionReason =
	"stuck" | "account-unknown" | "stripe-missing";

export interface RefundNeedingAttention {
	/** An order's refund; null on a Pay Tab refund (HA-984), which has {@link tabId}. */
	orderId: string | null;
	/** HA-984 — a Pay Tab refund: one Stripe refund over every order it was spread over, resolved on the tab. */
	tabId?: string | null;
	refundId: string;
	total: Money;
	method: string;
	status: string;
	attempt: number;
	stripeAccount: string | null;
	stripeRefundId: string | null;
	stripePaymentIntentId: string | null;
	processingSince: string | null;
	reason: RefundAttentionReason;
	/** What the reconciler checked, shown as detail. */
	evidence: string;
	since: string;
}

export type RefundResolution = "succeeded" | "failed";

export interface ResolveRefundInput {
	outcome: RefundResolution;
	/** At least {@link MIN_EVIDENCE} characters: what shows it (a Stripe entry, a bank reference…). */
	evidence: string;
	/** Only with "succeeded"; left out for a refund made outside Stripe. */
	stripeRefundId?: string;
}

export const MIN_EVIDENCE = 10;

export const REASON_COPY: Record<RefundAttentionReason, string> = {
	stuck: "Still processing after a day",
	"account-unknown": "We can't tell which Stripe account this refund is on",
	"stripe-missing": "Stripe can't find this refund on the account",
};

export const refundsAttentionApi = {
	async list(tenant: string, venueId: string) {
		const url = new URL(
			`/venue-management/${venueId}/epos/refunds/needs-attention`,
			apiUrl,
		);
		return await fetchJson<{ refunds: RefundNeedingAttention[] }>(
			url.toString(),
			{
				method: "GET",
				headers: { tenant },
			},
		);
	},

	listOptions: (tenant: string, venueId: string) =>
		queryOptions({
			queryKey: [tenant, venueId, "epos", "refunds-needing-attention"],
			queryFn: () => refundsAttentionApi.list(tenant, venueId),
		}),

	/**
	 * Resolves an order's refund, or HA-984 a Pay Tab refund on its tab (one Stripe refund over every order it was spread
	 * over; the server settles every part with it and refuses a part on its order).
	 */
	async resolve(
		tenant: string,
		venueId: string,
		refund: Pick<RefundNeedingAttention, "orderId" | "tabId" | "refundId">,
		input: ResolveRefundInput,
	) {
		const url = new URL(
			refund.tabId
				? `/venue-management/${venueId}/epos/tabs/${refund.tabId}/refunds/${refund.refundId}/resolve`
				: `/venue-management/${venueId}/epos/orders/${refund.orderId}/refunds/${refund.refundId}/resolve`,
			apiUrl,
		);
		return await fetchJson<{
			refundId: string;
			status: string;
		}>(url.toString(), {
			method: "POST",
			headers: { tenant },
			body: JSON.stringify(input),
		});
	},
};

/** The refusal codes after which the list is out of date and should be refetched. */
export const STALE_CODES = new Set([
	"REFUND_NOT_AWAITING_RESOLUTION",
	"REFUND_CHANGED",
	"REFUND_NOT_FOUND",
	"TAB_REFUND_PART",
]);

/** The ProblemDetails `code` of a refusal (extensions flatten to the root on the wire). */
export function refusalCode(error: unknown): string | undefined {
	return error instanceof HttpRequestError
		? (error.problem?.code as string | undefined)
		: undefined;
}

/** What to tell the operator when a resolution is refused, by the contract's codes. */
export function resolveRefusal(error: unknown): string {
	const problem = error instanceof HttpRequestError ? error.problem : undefined;
	const stripeStatus = problem?.stripeStatus as string | undefined;
	const stripeRefundId = problem?.stripeRefundId as string | undefined;

	switch (refusalCode(error)) {
		case "EVIDENCE_REQUIRED":
			return `Say what shows it: at least ${MIN_EVIDENCE} characters (a Stripe dashboard entry, a bank transfer reference…).`;
		case "RESOLVE_OUTCOME":
			return "Choose whether the refund succeeded or failed.";
		case "REFUND_NOT_FOUND":
			return "This refund can't be found any more. The list has been refreshed.";
		case "REFUND_NOT_AWAITING_RESOLUTION":
			return "This refund no longer needs a person. The list has been refreshed.";
		case "REFUND_CHANGED":
			return "This refund changed while you were resolving it. The list has been refreshed; check it and try again.";
		case "STRIPE_ACCOUNT_UNKNOWN":
			return "It can't be checked with Stripe, because we don't know which account it's on. You can still mark it succeeded without a Stripe refund id, with evidence.";
		case "STRIPE_DISAGREES":
			return `Stripe doesn't back that up: the Stripe refund is missing, isn't this refund's, or failed${stripeStatus ? ` (Stripe says: ${stripeStatus})` : ""}. Check the Stripe refund id.`;
		case "STRIPE_HAS_REFUND":
			return `Stripe has this refund${stripeRefundId ? ` (${stripeRefundId})` : ""}${stripeStatus ? `, ${stripeStatus}` : ""}, so it can't be marked failed. If the customer got the money back, mark it succeeded instead.`;
		case "NO_PAYMENT_INTENT":
			return "It can't be marked failed: there's no Stripe payment recorded to check it against.";
		case "TAB_REFUND_PART":
			return "This is part of a Pay Tab refund: resolve the Pay Tab refund, which settles every order it was spread over. The list has been refreshed.";
		case "STRIPE_UNAVAILABLE":
			return "Stripe couldn't be fully checked just now. Try again later.";
		default:
			return problem?.detail ?? "That didn't work. Try again.";
	}
}
